import { useCallback, useState } from "react";
import type { RegisterStudentResult, StartGameSessionResult } from "@/types/game";

const STUDENT_KEY = "weldquest.student";
const SESSION_KEY = "weldquest.session";

export interface StoredStudent {
  student_id: string;
  full_name: string;
  email: string;
  school_id: string | null;
  school_name: string | null;
}

function loadStudent(): StoredStudent | null {
  try {
    const s = sessionStorage.getItem(STUDENT_KEY);
    return s ? (JSON.parse(s) as StoredStudent) : null;
  } catch { return null; }
}
function loadSession(): StartGameSessionResult | null {
  try {
    const s = sessionStorage.getItem(SESSION_KEY);
    return s ? (JSON.parse(s) as StartGameSessionResult) : null;
  } catch { return null; }
}

/**
 * Local-only convenience cache so the student doesn't have to re-enter their
 * info if they refresh between Register → Play → Results in the same browser.
 *
 * NOT a source of truth — the database is. Anything sensitive (scores,
 * answers) is fetched server-side.
 *
 * Lazy-init from sessionStorage so the first render of any consumer sees
 * the right value (prevents StudentGamePage from bouncing back to register
 * before sessionStorage is read).
 */
export function useStudentSession() {
  const [student, setStudentState] = useState<StoredStudent | null>(loadStudent);
  const [session, setSessionState] = useState<StartGameSessionResult | null>(loadSession);

  const setStudent = useCallback((s: StoredStudent | null) => {
    setStudentState(s);
    if (s) sessionStorage.setItem(STUDENT_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(STUDENT_KEY);
  }, []);

  const setSession = useCallback((s: StartGameSessionResult | null) => {
    setSessionState(s);
    if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(SESSION_KEY);
  }, []);

  const recordRegistration = useCallback(
    (input: { full_name: string; email: string; school_name: string | null }, result: RegisterStudentResult) => {
      const stored: StoredStudent = {
        student_id: result.student_id,
        full_name: input.full_name,
        email: input.email,
        school_id: result.school_id,
        school_name: input.school_name,
      };
      setStudent(stored);
      return stored;
    },
    [setStudent]
  );

  const clear = useCallback(() => {
    setStudent(null);
    setSession(null);
  }, [setStudent, setSession]);

  return { student, session, setStudent, setSession, recordRegistration, clear };
}
